import { useRef, useState, type ReactNode } from 'react';
import { UMIcon } from './UMIcon';
import { UMCheckbox, UMSidePanel } from './UMSidePanel';
import { UMButton, UMCheckboxTree, UMFormGroup, UMFormStyles, UMInput, UMMessage, UMSectionTitle, UMSelect, type UMCheckboxTreeNode } from './UMForm';
import { UMDrawToolbar, UMStack, UMToolGroup, type UMToolSpec } from './UMTools';
import { UMR } from './referenceTokens';

/**
 * Rail panels of https://urbanmap.myengie.com (measured 2026-10): Géométries, Mesures,
 * Sélection, Export, Import. Frame = <UMSidePanel> (300px, header 45px, body p-3).
 * Spec: docs/urbanmap/reference/panels.md. Panels are UI only: wire map drawing in the prototype.
 */

// ─── Géométries ────────────────────────────────────────────────────────────────

export const UM_GEOMETRY_TOOLS: UMToolSpec[][] = [
  [{ id: 'create', icon: 'linked_services', label: 'Créer' }],
  [
    { id: 'edit', icon: 'rebase_edit', label: 'Modifier' },
    { id: 'cut', icon: 'content_cut', label: 'Découper' },
  ],
  [
    { id: 'merge', icon: 'stack_group', label: 'Fusionner' },
    { id: 'unmerge', icon: 'split_scene', label: 'Défusionner' },
  ],
];

export interface UMGeometriesPanelProps {
  onClose: () => void;
  activeTool?: string | null;
  onToolChange?: (id: string) => void;
  /** Production enables Modifier/Découper/Fusionner/Défusionner once a building is drawn or selected. */
  hasBuilding?: boolean;
}

export function UMGeometriesPanel({ onClose, activeTool = null, onToolChange, hasBuilding = false }: UMGeometriesPanelProps) {
  return (
    <UMSidePanel title="Géométries des bâtiments" onClose={onClose}>
      <UMFormStyles />
      <UMStack>
        <UMSectionTitle>Tracé de bâtiment</UMSectionTitle>
        {UM_GEOMETRY_TOOLS.map((group, i) => (
          <UMToolGroup
            key={i}
            tools={group.map((t) => ({ ...t, disabled: i > 0 && !hasBuilding }))}
            activeId={activeTool}
            onSelect={onToolChange}
          />
        ))}
        {!hasBuilding && <UMMessage title="Pour activer plus d'options, dessinez un bâtiment (ou sélectionnez un existant)." />}
      </UMStack>
    </UMSidePanel>
  );
}

// ─── Mesures ───────────────────────────────────────────────────────────────────

export const UM_MEASURE_TOOLS: UMToolSpec[] = [
  { id: 'length', icon: 'straighten', label: 'Mesurer une longueur' },
  { id: 'area', icon: 'select', label: 'Mesurer une surface' },
  { id: 'erase', icon: 'ink_eraser', label: 'Supprimer des éléments' },
];

const fmt = (n: number) => n.toLocaleString('fr-FR', { maximumFractionDigits: 2 });

export interface UMMeasuresPanelProps {
  onClose: () => void;
  activeTool?: string | null;
  onToolChange?: (id: string) => void;
  /** Metres. */
  totalLength?: number;
  /** Square metres. */
  totalArea?: number;
}

export function UMMeasuresPanel({ onClose, activeTool = null, onToolChange, totalLength = 0, totalArea = 0 }: UMMeasuresPanelProps) {
  return (
    <UMSidePanel title="Mesures des éléments" onClose={onClose}>
      <UMFormStyles />
      <UMStack>
        <UMDrawToolbar tools={UM_MEASURE_TOOLS} activeId={activeTool} onSelect={onToolChange} />
        <div>Longueur totale : {fmt(totalLength)} m</div>
        <div>Superficie totale : {fmt(totalArea)} m²</div>
      </UMStack>
    </UMSidePanel>
  );
}

// ─── Sélection ─────────────────────────────────────────────────────────────────

export interface UMSelectionSummary {
  buildings: number;
  /** MWhu */
  heatTotal: number;
  /** Linear metres of drawn network. */
  pipes: number;
}

function SummaryRow({ label, value, unit }: { label: string; value: number; unit?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
      <div style={{ fontSize: 14, lineHeight: '20px', color: UMR.textStrong }}>{label}</div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.25 }}>
        <div style={{ fontSize: 16, lineHeight: '24px', fontWeight: 700, color: UMR.textStrong }}>{value.toLocaleString('fr-FR')}</div>
        {unit && <div style={{ fontSize: 12, lineHeight: UMR.lhXs, color: UMR.textMuted }}>{unit}</div>}
      </div>
    </div>
  );
}

export interface UMSelectionPanelProps {
  onClose: () => void;
  summary: UMSelectionSummary;
  activeTool?: string | null;
  onToolChange?: (id: string) => void;
}

/** "Contenu de la sélection" — enabled in the rail only when a folder is chosen in the toolbar. */
export function UMSelectionPanel({ onClose, summary, activeTool = null, onToolChange }: UMSelectionPanelProps) {
  return (
    <UMSidePanel title="Contenu de la sélection" onClose={onClose}>
      <UMFormStyles />
      <UMStack gap={16}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '8px 16px', background: UMR.grey100, borderRadius: 8, border: `1px solid ${UMR.border}` }}>
          <div style={{ fontSize: 12, lineHeight: UMR.lhXs, fontWeight: 500, color: UMR.textLabel }}>Résumé</div>
          <UMStack gap={16}>
            <SummaryRow label="Nombre de bâtiments" value={summary.buildings} />
            <SummaryRow label="Total chaud" value={summary.heatTotal} unit="MWhu" />
            <SummaryRow label="Canalisations" value={summary.pipes} unit="ml" />
          </UMStack>
        </div>
        <UMStack>
          <UMSectionTitle>Bâtiments</UMSectionTitle>
          <UMToolGroup label="Ajouter" activeId={activeTool} onSelect={onToolChange} tools={[{ id: 'add-one', icon: 'domain_add', label: 'Un par un' }, { id: 'add-zone', icon: 'highlight_alt', label: 'Par zone' }]} />
          <UMToolGroup label="Retirer" activeId={activeTool} onSelect={onToolChange} tools={[{ id: 'remove-one', icon: 'domain_disabled', label: 'Un par un' }, { id: 'remove-zone', icon: 'remove_selection', label: 'Par zone' }]} />
        </UMStack>
        <UMStack>
          <UMSectionTitle>Tracé de réseau</UMSectionTitle>
          <UMToolGroup label="Ajouter" activeId={activeTool} onSelect={onToolChange} tools={[{ id: 'draw-pipe', icon: 'polyline', label: 'Tracer' }]} />
          <UMToolGroup label="Supprimer" activeId={activeTool} onSelect={onToolChange} tools={[{ id: 'erase-pipe-one', icon: 'ink_eraser', label: 'Un par un' }, { id: 'erase-pipe-zone', icon: 'remove_selection', label: 'Par zone' }]} />
        </UMStack>
      </UMStack>
    </UMSidePanel>
  );
}

// ─── Export ────────────────────────────────────────────────────────────────────

export const UM_EXPORT_TREE: UMCheckboxTreeNode[] = [
  {
    id: 'buildings',
    label: 'Bâtiments',
    children: [
      { id: 'MainCharacteristics', label: 'Caractéristiques principales', locked: true },
      { id: 'SelectedEnergyAndConsumption', label: 'Énergies et conso retenues', locked: true },
      { id: 'DetailedCharacteristics', label: 'Caractéristiques détaillées' },
      {
        id: 'ConsumptionHistory',
        label: 'Historique de conso',
        children: ['Retenues', 'Renseignées', 'ENGIE', 'DLE GRDF', 'DLE ENEDIS', 'DLE Réseau', 'Ratio'].map((label) => ({ id: `history-${label}`, label })),
      },
      { id: 'OpenData', label: 'Toutes les données OpenData' },
    ],
  },
];

export const UM_EXPORT_DEFAULT_CHECKED = ['MainCharacteristics', 'SelectedEnergyAndConsumption'];

export interface UMExportPanelProps {
  onClose: () => void;
  /** Never call a real export from a prototype: show a toast / mock instead. */
  onExport?: (request: { perimeter: string; format: 'xlsx' | 'geojson'; data: string[] }) => void;
  onHistory?: () => void;
  /** Active toolbar selection (folder name). Replaces the Périmètre search and adds "Réseaux > Canalisations projet". */
  selectionLabel?: string | null;
}

export function UMExportPanel({ onClose, onExport, onHistory, selectionLabel }: UMExportPanelProps) {
  const [perimeter, setPerimeter] = useState('');
  const [format, setFormat] = useState<'xlsx' | 'geojson'>('xlsx');
  const [data, setData] = useState<string[]>(UM_EXPORT_DEFAULT_CHECKED);
  const [pipes, setPipes] = useState(true);
  const scope = selectionLabel ? `selection:${selectionLabel}` : perimeter.trim();
  return (
    <UMSidePanel
      title="Export des données"
      onClose={onClose}
      onHistory={onHistory}
      footer={
        <>
          <UMButton variant="secondary" onClick={onClose}>Fermer</UMButton>
          <UMButton disabled={!scope} onClick={() => onExport?.({ perimeter: scope, format, data: selectionLabel && pipes ? [...data, 'SelectionPipes'] : data })}>Exporter</UMButton>
        </>
      }
    >
      <UMFormStyles />
      <UMStack gap={16}>
        <UMStack>
          <UMSectionTitle>Contenu de l'export</UMSectionTitle>
          {selectionLabel ? (
            <UMFormGroup label="Contenu de l'export">
              <div style={{ fontSize: 14, lineHeight: '20px', color: UMR.blueLink }}>Sélection : {selectionLabel}</div>
            </UMFormGroup>
          ) : (
            <UMFormGroup label="Périmètre">
              <UMInput value={perimeter} onChange={setPerimeter} placeholder="Rechercher une ville" icon="search" />
            </UMFormGroup>
          )}
        </UMStack>
        <UMStack>
          <UMSectionTitle>Format et Données</UMSectionTitle>
          <UMFormGroup label="Format">
            <UMSelect ariaLabel="Format" value={format} onChange={(v) => setFormat(v as 'xlsx' | 'geojson')} options={[{ value: 'xlsx', label: 'Excel' }, { value: 'geojson', label: 'GeoJSON' }]} />
          </UMFormGroup>
          <UMFormGroup label="Données à exporter">
            <UMCheckboxTree nodes={UM_EXPORT_TREE} checked={data} onChange={setData} />
          </UMFormGroup>
          {selectionLabel && (
            <UMFormGroup label="Réseaux">
              <div style={{ display: 'inline-flex', alignSelf: 'flex-start' }}>
                <UMCheckbox label="Canalisations projet" checked={pipes} onChange={setPipes} />
              </div>
            </UMFormGroup>
          )}
        </UMStack>
      </UMStack>
    </UMSidePanel>
  );
}

// ─── Import ────────────────────────────────────────────────────────────────────

export interface UMImportPanelProps {
  onClose: () => void;
  /** Never upload from a prototype: receives the File for a mocked flow. */
  onImport?: (file: File) => void;
  onDownloadTemplate?: () => void;
  onHistory?: () => void;
}

function Dropzone({ file, onFile }: { file: File | null; onFile: (f: File) => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const line = (children: ReactNode, color: string = UMR.black) => <div style={{ color, textAlign: 'center' }}>{children}</div>;
  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        const f = e.dataTransfer.files[0];
        if (f) onFile(f);
      }}
      style={{
        height: 208, boxSizing: 'border-box', border: `1px dashed ${UMR.dropzoneBorder}`, padding: 20,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4,
        background: over ? UMR.bluePressed : 'transparent',
      }}
    >
      <UMIcon name="cloud_upload" size={48} color={UMR.textDisabled} />
      {file ? line(<strong>{file.name}</strong>) : line('Glissez-déposez votre document')}
      {line('ou', UMR.textSecondary)}
      <button type="button" onClick={() => input.current?.click()} style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', color: UMR.blueLink, fontFamily: UMR.font, fontSize: 14, lineHeight: 'inherit' }}>
        Cliquez pour parcourir
      </button>
      <div style={{ color: UMR.textSecondary }}>
        <div>Formats acceptés : .xlsx</div>
        <div>Taille max : 50 Mo</div>
      </div>
      <input ref={input} type="file" accept=".xlsx" hidden onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])} />
    </div>
  );
}

export function UMImportPanel({ onClose, onImport, onDownloadTemplate, onHistory }: UMImportPanelProps) {
  const [file, setFile] = useState<File | null>(null);
  return (
    <UMSidePanel
      title="Import de données"
      onClose={onClose}
      onHistory={onHistory}
      footer={
        <>
          <UMButton variant="secondary" onClick={onClose}>Fermer</UMButton>
          <UMButton disabled={!file} onClick={() => file && onImport?.(file)}>Importer</UMButton>
        </>
      }
    >
      <UMFormStyles />
      <UMStack gap={12}>
        <div>
          <div>Document source</div>
          <button type="button" onClick={onDownloadTemplate} style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', color: UMR.blueLink, fontFamily: UMR.font, fontSize: 14, lineHeight: '17px' }}>
            Télécharger le modèle d'import
          </button>
        </div>
        <Dropzone file={file} onFile={setFile} />
        <UMMessage>
          À noter :
          <ul style={{ listStyle: 'disc', margin: '4px 0 0', paddingLeft: 16 }}>
            <li>L'import s'appliquera à toute donnée dont la valeur Urban Map est différente de votre fichier.</li>
            <li>Les valeurs vérifiées par d'autres utilisateurs seront écrasées.</li>
            <li>Les nouvelles valeurs importées passeront au statut "vérifiées".</li>
          </ul>
        </UMMessage>
      </UMStack>
    </UMSidePanel>
  );
}
